<template>
  <div class="page">
    <PageHeader title="App Store" description="Discover new and existing apps - get connected!" />

    <section class="section content-container">
      <n-loading-spinner v-if="isLoading" />
      <div class="container is-fluid">
        <div class="columns is-3 is-multiline">
          <div
            v-for="app in apps"
            :key="app.id"
            class="column is-one-quarter-fullhd is-one-third-widescreen is-one-third-desktop is-half-tablet"
          >
            <div class="card has-equal-height" @click="showAppDetails(app)">
              <div class="card-content">
                <div class="media">
                  <div class="media-left">
                    <figure class="image is-48x48">
                      <svg class="app-icon">
                        <use xmlns:xlink="http://www.w3.org/1999/xlink" :xlink:href="`#${app.icon}`"></use>
                      </svg>
                    </figure>
                  </div>
                  <div class="media-content">
                    <p class="title is-4">
                      <strong>{{ app.name }}</strong>
                      <svg class="installed-icon" v-if="app.isInstalled">
                        <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#tick"></use>
                      </svg>
                    </p>
                    <p class="subtitle is-6">{{ app.category }}</p>
                  </div>
                </div>

                <div class="content">{{ app.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <router-view></router-view>
  </div>
</template>

<script lang="ts" setup>
import { appsApi } from '../services/apps';
import { PublishedApp } from '../models';
import PageHeader from '@/shared/components/pageHeader/PageHeader.vue';
import { SET_CURRENT_APP, HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import appInsights from '@/shared/services/appInsights';
import { computed, onBeforeMount, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const router = useRouter();
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const setCurrentApp = (app: PublishedApp) => store.dispatch(SET_CURRENT_APP, app);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const apps = ref<PublishedApp[]>([]);
const isLoading = ref(true);

onBeforeMount(async () => {
  if (!currentWorkspace.value) {
    await handleSeriousError('No workspace selected');
    return;
  }

  try {
    const loadedApps = await appsApi.listPublished(currentWorkspace.value.id);
    apps.value = loadedApps.filter((a) => !isHidden(a));
  } catch (err: any) {
    await handleSeriousError(err.message);
  } finally {
    isLoading.value = false;
  }
});

function showAppDetails(app: PublishedApp) {
  setCurrentApp(app);
  appInsights.trackEvent({ name: 'App viewed' }, { appId: app.id, workspaceId: currentWorkspace.value.id });
  router.push({ name: 'app', params: { id: app.id } });
}

function isHidden(app: PublishedApp): boolean {
  return app.status === 'hidden';
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.app-icon {
  width: 3rem;
  height: 3rem;
}
.installed-icon {
  width: 2rem;
  height: 2rem;
  vertical-align: bottom;
}

.card {
  cursor: pointer;

  &:hover {
    box-shadow:
      0 0 20px rgba(10, 10, 10, 0.2),
      0 0 0 1px rgba(10, 10, 10, 0.1);
  }
}

.has-equal-height {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
